import React, { useState, useEffect } from 'react';
import { X, Save, TrendingDown, IndianRupee, Calendar, FileText, Building, Tag } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { expenseService } from '../../lib/database/expenses';

interface EditExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  expense: any;
}

const commonCategories = [
  'Office Supplies',
  'Travel',
  'Marketing',
  'Software',
  'Equipment',
  'Utilities',
  'Rent',
  'Insurance',
  'Professional Services',
  'Meals & Entertainment',
  'Training',
  'Other'
];

export const EditExpenseModal: React.FC<EditExpenseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  expense,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');

  // Initialize form with expense data
  useEffect(() => {
    if (expense) {
      setDate(expense.date || '');
      
      // Check if category is in common categories
      const isCommonCategory = commonCategories.includes(expense.category);
      if (isCommonCategory) {
        setCategory(expense.category);
        setCustomCategory('');
      } else {
        setCategory('custom');
        setCustomCategory(expense.category || '');
      }
      
      setVendor(expense.vendor || '');
      setAmount(expense.amount?.toString() || '');
      setNotes(expense.notes || '');
      setReceiptUrl(expense.receipt_url || '');
    }
  }, [expense]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!date) {
        setError('Please select a date');
        return;
      }

      const finalCategory = category === 'custom' ? customCategory.trim() : category;
      if (!finalCategory) {
        setError('Please select or enter a category');
        return;
      }

      if (!vendor.trim()) {
        setError('Please enter a vendor name');
        return;
      }

      if (!amount || Number(amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      // Update expense
      const { data: updatedExpense, error: expenseError } = await expenseService.updateExpense(expense.id, {
        date,
        category: finalCategory,
        vendor: vendor.trim(),
        amount: Number(amount),
        notes: notes.trim() || null,
        receipt_url: receiptUrl.trim() || null,
      });

      if (expenseError || !updatedExpense) {
        setError('Failed to update expense: ' + (expenseError?.message || 'Unknown error'));
        return;
      }

      setSuccess('Expense updated successfully!');
      
      // Close modal and refresh after delay
      setTimeout(() => {
        onSuccess();
        onClose();
        setError('');
        setSuccess('');
      }, 1500);

    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !expense) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Edit Expense</h2>
              <p className="text-gray-600">Update expense details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <Alert type="error" message={error} />}
          {success && <Alert type="success" message={success} />}

          {/* Expense ID Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Expense ID</p>
            <p className="text-lg font-semibold text-gray-900">#{expense.id.slice(-8)}</p>
          </div>

          {/* Date and Amount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Date" icon={Calendar} required>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </FormField>

            <FormField label="Amount (₹)" icon={IndianRupee} required>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
                min="0.01"
                step="0.01"
                required
              />
            </FormField>
          </div>

          {/* Category */}
          <FormField label="Category" icon={Tag} required>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="">Select a category</option>
              {commonCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
              <option value="custom">Custom Category</option>
            </select>
          </FormField>

          {/* Custom Category */}
          {category === 'custom' && (
            <FormField label="Custom Category" icon={Tag} required>
              <input
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter custom category"
                required
              />
            </FormField>
          )}

          {/* Vendor */}
          <FormField label="Vendor" icon={Building} required>
            <input
              type="text"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Vendor or supplier name"
              required
            />
          </FormField>

          {/* Receipt URL */}
          <FormField label="Receipt URL" helpText="Optional link to receipt or invoice">
            <input
              type="url"
              value={receiptUrl}
              onChange={(e) => setReceiptUrl(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://example.com/receipt.pdf"
            />
          </FormField>

          {/* Notes */}
          <FormField label="Notes" icon={FileText} helpText="Optional expense description">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Additional details about this expense..."
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              icon={Save}
              loading={loading}
              disabled={loading}
            >
              Update Expense
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};